import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
  Pencil, Save, Plus, CheckCircle2, XCircle,
  Download, Trash2, FileText, Users, AlertTriangle,
  FileSpreadsheet, CloudUpload, ChevronDown, ChevronUp,
  Building2, Globe, MapPin, Phone, Mail, AtSign,
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import adminService from '../../services/adminService';

// ─── Constants ────────────────────────────────────────────────────────────────
const LICENSE_STATUSES     = ['Active','Inactive','Pending','Suspended','Expired','Revoked','Cancelled'];
const LICENSE_TYPES        = ['Cultivation','Processing','Retail','Distribution','Laboratory Testing','Manufacturing','Dispensary','Delivery','Wholesale','Microbusiness','Other'];
const LICENSE_DESIGNATIONS = ['Tier 1','Tier 2','Tier 3','Standard','Medical Only','Adult Use Only','Dual Use','Provisional','Annual','Conditional','Other'];
const LICENSE_VERTICALS    = ['Cannabis','Hemp','Agriculture','CBD','Other'];
const LICENSE_ACTIVITIES   = ['Cultivation','Retail Sales','Manufacturing','Distribution','Testing','Research','Delivery','Wholesale','Other'];
const LICENSE_CATEGORIES   = ['Medical','Adult Use','Recreational','Dual Use','Research','Other'];
const CONTACT_DESIGNATIONS = ['Owner','CEO','President','Manager','Compliance Officer','Operations Manager','Contact Person','Agent','Other'];

const STATUS_COLORS = {
  Active:    'bg-emerald-100 text-emerald-700',
  Inactive:  'bg-gray-100 text-gray-600',
  Pending:   'bg-yellow-100 text-yellow-700',
  Suspended: 'bg-orange-100 text-orange-700',
  Expired:   'bg-red-100 text-red-700',
  Revoked:   'bg-red-100 text-red-700',
  Cancelled: 'bg-gray-100 text-gray-500',
};

const PHONE_RE = /^\+?[\d\s\-().]{7,20}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Templates ────────────────────────────────────────────────────────────────
const EMPTY_CONTACT = {
  contactDesignation: '', contactFirstName: '', contactLastName: '',
  contactPhone: '', contactEmail: '',
};

const EMPTY_LICENSE = {
  licenseNumber: '', licenseStateId: null, licenseType: '', licenseDesignation: '',
  licenseStatus: '', category: '', vertical: '', activityOrFacility: '',
  licenseTerm: '', issueDate: '', expirationDate: '', firstOccurrence: '',
  renewalDueDate: '', operationalStatus: '',
  businessName: '', dbaName: '', email: '', phone: '', website: '',
  streetAddress: '', city: '', county: '', zipCode: '', addressStateId: null,
  mailingAddress: '', instagram: '', facebook: '', linkedin: '', twitter: '',
  latitude: '', longitude: '',
  contacts: [{ ...EMPTY_CONTACT }],
};

function newOperator() {
  return {
    corporateName: '', corpPhone: '', corpEmail: '', corpWebsite: '',
    corpFirstName: '', corpLastName: '', corpDesignation: '',
    corpFB: '', corpInsta: '', corpTwitter: '', corpLinkedIn: '',
    notes: '',
    licenses: [{ ...EMPTY_LICENSE, contacts: [{ ...EMPTY_CONTACT }] }],
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function optionItems(list, cur) {
  const all = cur && !list.includes(cur) ? [...list, cur] : list;
  return all.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>);
}
function ComboSelect({ value, onValueChange, options, placeholder }) {
  return (
    <Select value={value || '__none__'} onValueChange={v => onValueChange(v === '__none__' ? '' : v)}>
      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— None —</SelectItem>
        {optionItems(options, value)}
      </SelectContent>
    </Select>
  );
}
function Err({ msg }) { return msg ? <p className="text-xs text-destructive mt-0.5">{msg}</p> : null; }
function SectionLabel({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs font-semibold uppercase text-muted-foreground border-b pb-1 mb-2">
      {Icon && <Icon className="w-3 h-3" />}{label}
    </div>
  );
}
function F({ label, value, onChange, type = 'text', placeholder, span, required }) {
  return (
    <div className={`space-y-1 ${span || ''}`}>
      <Label className="text-xs">{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
      <Input type={type} placeholder={placeholder} value={value ?? ''} onChange={e => onChange(e.target.value)} className="h-8 text-sm" />
    </div>
  );
}

// ─── ContactItem ──────────────────────────────────────────────────────────────
function ContactItem({ contact, index, onChange, onRemove, canRemove }) {
  const set = (f, v) => onChange(index, { ...contact, [f]: v });
  const phoneErr = contact.contactPhone?.trim() && !PHONE_RE.test(contact.contactPhone.trim());
  const emailErr = contact.contactEmail?.trim() && !EMAIL_RE.test(contact.contactEmail.trim());
  return (
    <div className="border rounded-lg p-3 bg-background space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">Contact {index + 1}</span>
        {canRemove && (
          <Button variant="ghost" size="sm" onClick={() => onRemove(index)}
            className="h-5 w-5 p-0 text-destructive hover:text-destructive">
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-xs">Designation</Label>
          <ComboSelect value={contact.contactDesignation} onValueChange={v => set('contactDesignation', v)} options={CONTACT_DESIGNATIONS} placeholder="Role/Title" />
        </div>
        <F label="First Name" value={contact.contactFirstName} onChange={v => set('contactFirstName', v)} />
        <F label="Last Name"  value={contact.contactLastName}  onChange={v => set('contactLastName', v)} />
        <div className="space-y-1">
          <Label className="text-xs">Phone</Label>
          <Input placeholder="(555) 000-0000" value={contact.contactPhone ?? ''} onChange={e => set('contactPhone', e.target.value)}
            className={`h-8 text-sm ${phoneErr ? 'border-destructive' : ''}`} />
          <Err msg={phoneErr ? 'Invalid phone' : ''} />
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-xs">Email</Label>
          <Input type="email" value={contact.contactEmail ?? ''} onChange={e => set('contactEmail', e.target.value)}
            className={`h-8 text-sm ${emailErr ? 'border-destructive' : ''}`} />
          <Err msg={emailErr ? 'Invalid email' : ''} />
        </div>
      </div>
    </div>
  );
}

// ─── LicenseItem ──────────────────────────────────────────────────────────────
function LicenseItem({ license, index, onChange, onRemove, canRemove, states }) {
  const [open, setOpen]           = useState(index === 0);
  const [section, setSection]     = useState('license'); // license | business | address | social | contacts
  const set    = (f, v) => onChange(index, { ...license, [f]: v });
  const contacts = license.contacts ?? [];
  const updCon = (i, v) => set('contacts', contacts.map((c, j) => j === i ? v : c));
  const rmCon  = i       => set('contacts', contacts.filter((_, j) => j !== i));
  const addCon = ()      => set('contacts', [...contacts, { ...EMPTY_CONTACT }]);

  const stateSelector = (field, value) => (
    <Select value={value != null ? String(value) : '__none__'}
      onValueChange={v => set(field, v === '__none__' ? null : Number(v))}>
      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select state" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="__none__">— None —</SelectItem>
        {states.map(s => <SelectItem key={s.stateId} value={String(s.stateId)}>{s.abbreviation} — {s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  const TAB = ({ id, label }) => (
    <button
      type="button"
      onClick={() => setSection(id)}
      className={`px-2.5 py-1 text-xs rounded font-medium transition-colors
        ${section === id ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
    >{label}</button>
  );

  return (
    <div className="border rounded-xl overflow-hidden shadow-sm">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2.5 bg-muted/40 cursor-pointer hover:bg-muted/60 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">
            {index + 1}
          </span>
          <span className="text-sm font-semibold truncate">
            {license.licenseNumber || `License ${index + 1}`}
          </span>
          {license.licenseStateId && states.find(s => s.stateId === license.licenseStateId) && (
            <Badge variant="secondary" className="text-xs shrink-0">
              {states.find(s => s.stateId === license.licenseStateId)?.abbreviation}
            </Badge>
          )}
          {license.licenseStatus && (
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${STATUS_COLORS[license.licenseStatus] || 'bg-muted text-muted-foreground'}`}>
              {license.licenseStatus}
            </span>
          )}
          {license.businessName && (
            <span className="text-xs text-muted-foreground truncate hidden sm:block">{license.businessName}</span>
          )}
          <span className="text-xs text-muted-foreground shrink-0">
            {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {canRemove && (
            <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); onRemove(index); }}
              className="h-6 w-6 p-0 text-destructive hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
        </div>
      </div>

      {open && (
        <div className="bg-muted/10">
          {/* Section tabs */}
          <div className="flex gap-1 px-4 py-2 border-b bg-muted/20 flex-wrap">
            <TAB id="license"   label="License Info" />
            <TAB id="business"  label="Business" />
            <TAB id="address"   label="Address" />
            <TAB id="social"    label="Social" />
            <TAB id="contacts"  label={`Contacts (${contacts.length})`} />
          </div>

          <div className="p-4">
            {/* ── License Info ── */}
            {section === 'license' && (
              <div className="grid grid-cols-2 gap-3">
                <F label="License Number" value={license.licenseNumber} onChange={v => set('licenseNumber', v)} span="col-span-2" placeholder="LIC-000000" />
                <div className="space-y-1">
                  <Label className="text-xs">License State</Label>
                  {stateSelector('licenseStateId', license.licenseStateId)}
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Status</Label>
                  <ComboSelect value={license.licenseStatus} onValueChange={v => set('licenseStatus', v)} options={LICENSE_STATUSES} placeholder="Status" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Type</Label>
                  <ComboSelect value={license.licenseType} onValueChange={v => set('licenseType', v)} options={LICENSE_TYPES} placeholder="Type" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Designation</Label>
                  <ComboSelect value={license.licenseDesignation} onValueChange={v => set('licenseDesignation', v)} options={LICENSE_DESIGNATIONS} placeholder="Designation" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Category</Label>
                  <ComboSelect value={license.category} onValueChange={v => set('category', v)} options={LICENSE_CATEGORIES} placeholder="Category" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Vertical</Label>
                  <ComboSelect value={license.vertical} onValueChange={v => set('vertical', v)} options={LICENSE_VERTICALS} placeholder="Vertical" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Activity / Facility</Label>
                  <ComboSelect value={license.activityOrFacility} onValueChange={v => set('activityOrFacility', v)} options={LICENSE_ACTIVITIES} placeholder="Activity" />
                </div>
                <F label="License Term"        value={license.licenseTerm}      onChange={v => set('licenseTerm', v)}      placeholder="e.g. 1 Year" />
                <F label="Operational Status"  value={license.operationalStatus} onChange={v => set('operationalStatus', v)} />
                <F label="Issue Date"          value={license.issueDate}         onChange={v => set('issueDate', v)}         type="date" />
                <F label="Expiration Date"     value={license.expirationDate}    onChange={v => set('expirationDate', v)}    type="date" />
                <F label="First Occurrence"    value={license.firstOccurrence}   onChange={v => set('firstOccurrence', v)}   type="date" />
                <F label="Renewal Due Date"    value={license.renewalDueDate}    onChange={v => set('renewalDueDate', v)}    type="date" />
              </div>
            )}

            {/* ── Business Info ── */}
            {section === 'business' && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Business Name" value={license.businessName} onChange={v => set('businessName', v)} span="col-span-2" placeholder="Operating business name" />
                <F label="DBA Name"      value={license.dbaName}      onChange={v => set('dbaName', v)}      span="col-span-2" placeholder="Doing business as" />
                <F label="Email"         value={license.email}         onChange={v => set('email', v)}         type="email" placeholder="business@example.com" />
                <F label="Phone"         value={license.phone}         onChange={v => set('phone', v)}         placeholder="(555) 000-0000" />
                <F label="Website"       value={license.website}       onChange={v => set('website', v)}       span="col-span-2" placeholder="https://" />
              </div>
            )}

            {/* ── Address ── */}
            {section === 'address' && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Street Address" value={license.streetAddress} onChange={v => set('streetAddress', v)} span="col-span-2" />
                <F label="City"           value={license.city}          onChange={v => set('city', v)} />
                <F label="County"         value={license.county}        onChange={v => set('county', v)} />
                <F label="Zip Code"       value={license.zipCode}       onChange={v => set('zipCode', v)} />
                <div className="space-y-1">
                  <Label className="text-xs">Address State</Label>
                  {stateSelector('addressStateId', license.addressStateId)}
                </div>
                <F label="Mailing Address" value={license.mailingAddress} onChange={v => set('mailingAddress', v)} span="col-span-2" />
                <F label="Latitude"  value={license.latitude}  onChange={v => set('latitude', v)}  type="number" placeholder="e.g. 39.7392" />
                <F label="Longitude" value={license.longitude} onChange={v => set('longitude', v)} type="number" placeholder="e.g. -104.9903" />
              </div>
            )}

            {/* ── Social ── */}
            {section === 'social' && (
              <div className="grid grid-cols-2 gap-3">
                <F label="Instagram" value={license.instagram} onChange={v => set('instagram', v)} placeholder="@handle or URL" />
                <F label="Facebook"  value={license.facebook}  onChange={v => set('facebook', v)}  placeholder="Page name or URL" />
                <F label="LinkedIn"  value={license.linkedin}  onChange={v => set('linkedin', v)}  placeholder="Profile or page URL" />
                <F label="Twitter / X" value={license.twitter} onChange={v => set('twitter', v)}   placeholder="@handle or URL" />
              </div>
            )}

            {/* ── Contacts ── */}
            {section === 'contacts' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-muted-foreground">{contacts.length} contact{contacts.length !== 1 ? 's' : ''} for this license</p>
                  <Button variant="outline" size="sm" onClick={addCon} className="h-7 text-xs">
                    <Plus className="h-3 w-3 mr-1" />Add Contact
                  </Button>
                </div>
                {contacts.map((c, i) => (
                  <ContactItem key={i} index={i} contact={c} onChange={updCon} onRemove={rmCon} canRemove={contacts.length > 1} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── OperatorCorporateFields ──────────────────────────────────────────────────
function OperatorCorporateFields({ form, onChange }) {
  const set = (f, v) => onChange(p => ({ ...p, [f]: v }));
  return (
    <div className="space-y-5">
      <div>
        <SectionLabel icon={Building2} label="Identity" />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1 col-span-2">
            <Label className="text-xs">Corporate Name <span className="text-destructive">*</span></Label>
            <Input placeholder="Legal corporate name (used for upsert matching)"
              value={form.corporateName ?? ''} onChange={e => set('corporateName', e.target.value)} className="h-8 text-sm" />
          </div>
        </div>
      </div>
      <div>
        <SectionLabel icon={Phone} label="Contact" />
        <div className="grid grid-cols-2 gap-3">
          <F label="Corp Phone"   value={form.corpPhone}   onChange={v => set('corpPhone', v)}   placeholder="(555) 000-0000" />
          <F label="Corp Email"   value={form.corpEmail}   onChange={v => set('corpEmail', v)}   type="email" placeholder="contact@company.com" />
          <F label="Website"      value={form.corpWebsite} onChange={v => set('corpWebsite', v)} placeholder="https://" span="col-span-2" />
        </div>
      </div>
      <div>
        <SectionLabel icon={Users} label="Primary Corporate Contact Person" />
        <div className="grid grid-cols-2 gap-3">
          <F label="Designation"  value={form.corpDesignation} onChange={v => set('corpDesignation', v)} placeholder="CEO, Owner, etc." />
          <div />{/* spacer */}
          <F label="First Name"   value={form.corpFirstName}   onChange={v => set('corpFirstName', v)} />
          <F label="Last Name"    value={form.corpLastName}    onChange={v => set('corpLastName', v)} />
        </div>
      </div>
      <div>
        <SectionLabel icon={AtSign} label="Social Media" />
        <div className="grid grid-cols-2 gap-3">
          <F label="Facebook"    value={form.corpFB}       onChange={v => set('corpFB', v)}       placeholder="Page URL" />
          <F label="Instagram"   value={form.corpInsta}    onChange={v => set('corpInsta', v)}    placeholder="@handle" />
          <F label="Twitter / X" value={form.corpTwitter}  onChange={v => set('corpTwitter', v)}  placeholder="@handle" />
          <F label="LinkedIn"    value={form.corpLinkedIn} onChange={v => set('corpLinkedIn', v)} placeholder="Profile URL" />
        </div>
      </div>
      <div>
        <SectionLabel label="Notes" />
        <Input placeholder="Internal notes…" value={form.notes ?? ''} onChange={e => set('notes', e.target.value)} className="h-8 text-sm" />
      </div>
    </div>
  );
}

// ─── OperatorDialog ───────────────────────────────────────────────────────────
function OperatorDialog({ open, onOpenChange, title, description, form, setForm, onSave, saving, states, addLicense, upLicense, rmLicense }) {
  const [tab, setTab] = useState('corporate');
  const TAB = ({ id, label, count }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors
        ${tab === id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`}
    >
      {label}
      {count != null && (
        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold
          ${tab === id ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={v => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {/* Stats bar (edit only) */}
        {form?.licenseCount != null && (
          <div className="flex gap-4 px-6 py-2 bg-muted/30 border-b text-xs text-muted-foreground">
            <span><strong className="text-foreground">{form.licenseStateCount ?? 0}</strong> state{(form.licenseStateCount ?? 0) !== 1 ? 's' : ''}</span>
            <span><strong className="text-foreground">{form.licenseCount ?? 0}</strong> license{(form.licenseCount ?? 0) !== 1 ? 's' : ''}</span>
            <span><strong className="text-foreground">{form.licensetypeCount ?? 0}</strong> type{(form.licensetypeCount ?? 0) !== 1 ? 's' : ''}</span>
          </div>
        )}

        {/* Tab bar */}
        <div className="flex border-b px-6 bg-background">
          <TAB id="corporate" label="Corporate Info" />
          <TAB id="licenses"  label="Licenses" count={form?.licenses?.length ?? 0} />
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {tab === 'corporate' && form && (
            <OperatorCorporateFields form={form} onChange={setForm} />
          )}
          {tab === 'licenses' && form && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">Each license can have multiple contacts</p>
                <Button variant="outline" size="sm" onClick={addLicense} className="h-7 text-xs">
                  <Plus className="h-3 w-3 mr-1" />Add License
                </Button>
              </div>
              {form.licenses.map((lic, i) => (
                <LicenseItem
                  key={i} index={i} license={lic}
                  onChange={upLicense} onRemove={rmLicense}
                  canRemove={form.licenses.length > 1} states={states}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 py-4 border-t bg-background">
          <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={onSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── BulkUploadModal ──────────────────────────────────────────────────────────
function BulkUploadModal({ open, onClose, onComplete }) {
  const [step, setStep]         = useState('upload');
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState([]);
  const [parseError, setParseError] = useState('');
  const [result, setResult]     = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setStep('upload'); setFile(null); setPreview([]); setParseError(''); setResult(null); };
  const handleClose = () => { reset(); onClose(); };

  const parseForPreview = (f) => {
    setParseError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'binary' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!rows.length) { setParseError('No data rows found.'); return; }
        const groups = {};
        rows.forEach(r => {
          const key = (r['CorporateName'] || '').trim();
          if (!key) return;
          if (!groups[key]) groups[key] = { corporateName: key, licenses: new Set(), contacts: 0 };
          if (r['LicenseNumber']) groups[key].licenses.add(`${r['LicenseNumber']}|${r['LicenseState'] || ''}`);
          if (r['ContactEmail'] || r['ContactFirstName']) groups[key].contacts++;
        });
        setPreview(Object.values(groups).map(g => ({ ...g, licenseCount: g.licenses.size })));
        setStep('preview');
      } catch { setParseError('Failed to parse file. Ensure it is a valid .xlsx / .xls file.'); }
    };
    reader.readAsBinaryString(f);
  };

  const handleFile = (f) => {
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext !== 'xlsx' && ext !== 'xls') { setParseError('Only .xlsx or .xls files are accepted.'); return; }
    setFile(f);
    parseForPreview(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setStep('uploading');
    try {
      const res = await adminService.bulkUploadOperators(file);
      setResult(res); setStep('results'); onComplete();
    } catch (err) {
      setResult({ created: 0, updated: 0, failed: 1, errors: [typeof err === 'string' ? err : 'Upload failed.'] });
      setStep('results');
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'CorporateName',
      'CorpPhone','CorpEmail','CorpWebsite',
      'CorpFirstName','CorpLastName','CorpDesignation',
      'CorpFB','CorpInsta','CorpTwitter','CorpLinkedIn','Notes',
      'LicenseNumber','LicenseState','LicenseType','LicenseDesignation','LicenseStatus',
      'Category','Vertical','ActivityOrFacility','LicenseTerm','OperationalStatus',
      'IssueDate','ExpirationDate','FirstOccurrence','RenewalDueDate',
      'LicBusinessName','LicDbaName','LicEmail','LicPhone','LicWebsite',
      'LicStreetAddress','LicCity','LicCounty','LicZipCode','LicState','MailingAddress',
      'Instagram','Facebook','Linkedin','Twitter','LicLatitude','LicLongitude',
      'ContactDesignation','ContactFirstName','ContactLastName','ContactPhone','ContactEmail',
    ];
    const ex1 = [
      'Green Leaf Cannabis Co.',
      '(555) 123-4567','info@greenleaf.com','https://greenleaf.com',
      'Jane','Smith','CEO',
      '','@greenleafco','','','Example operator',
      'LIC-2024-001','CO','Cultivation','Tier 1','Active',
      'Medical','Cannabis','Cultivation','1 Year','Operational',
      '2024-01-15','2025-01-14','2024-01-15','2024-12-01',
      'Green Leaf Denver','','contact@greenleaf.com','(555) 123-4567','',
      '123 Main St','Denver','Denver County','80203','CO','',
      '','','','','39.7392','-104.9903',
      'Owner','Jane','Smith','(555) 987-6543','jane@greenleaf.com',
    ];
    const ex2 = [
      'Green Leaf Cannabis Co.',
      '','','','','','','','','','','',
      'LIC-2024-001','CO','','','','','','','','','','','','',
      '','','','','','','','','','','','','','','','','',
      'Manager','Bob','Jones','(555) 111-2222','bob@greenleaf.com',
    ];
    const ex3 = [
      'Green Leaf Cannabis Co.',
      '','','','','','','','','','','',
      'LIC-2024-002','WA','Retail','Standard','Active',
      'Adult Use','Cannabis','Retail Sales','1 Year','Operational',
      '2024-03-01','2025-02-28','','',
      'Green Leaf Seattle','','seattle@greenleaf.com','(555) 333-4444','',
      '456 Pike St','Seattle','King County','98101','WA','',
      '','','','','47.6062','-122.3321',
      'Store Manager','Alice','Wong','(555) 333-4444','alice@greenleaf.com',
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ex1, ex2, ex3]);
    ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 4, 14) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Operators');
    XLSX.writeFile(wb, 'operators_bulk_template.xlsx');
    toast.success('Template downloaded');
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CloudUpload className="w-5 h-5 text-emerald-600" />Bulk Upload Operators</DialogTitle>
          <DialogDescription>One row per contact. Same CorporateName + LicenseNumber = multiple contacts on that license.</DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs">
          {[['upload','1','Upload'],['preview','2','Preview'],['results','3','Results']].map(([s, num, label], i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-1.5 font-medium ${step === s ? 'text-emerald-700' : 'text-muted-foreground'}`}>
                <span className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold ${step === s ? 'bg-emerald-600 text-white' : 'bg-muted'}`}>{num}</span>
                {label}
              </div>
              {i < 2 && <div className="flex-1 h-px bg-border" />}
            </React.Fragment>
          ))}
        </div>

        {step === 'upload' && (
          <div className="space-y-4">
            <div
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors
                ${isDragging ? 'border-emerald-500 bg-emerald-50' : 'border-border hover:border-emerald-400 hover:bg-emerald-50/40'}`}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={e => { e.preventDefault(); setIsDragging(false); handleFile(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
            >
              <CloudUpload className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
              <p className="font-semibold mb-1">Drop your Excel file here</p>
              <p className="text-sm text-muted-foreground">or click to browse — .xlsx / .xls</p>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleFile(e.target.files?.[0])} />
            </div>
            {parseError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{parseError}
              </div>
            )}
            <div className="flex items-center justify-between p-4 bg-muted/40 rounded-xl border border-dashed">
              <div>
                <p className="text-sm font-medium">Need a template?</p>
                <p className="text-xs text-muted-foreground">All column names with 3 example rows</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadTemplate}><Download className="w-4 h-4 mr-2" />Download</Button>
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800">
              <CheckCircle2 className="w-5 h-5 shrink-0" />
              <div>
                <p className="text-sm font-semibold">Parsed successfully</p>
                <p className="text-xs">{file?.name} · {preview.length} operator{preview.length !== 1 ? 's' : ''} detected</p>
              </div>
            </div>
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm border-collapse">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-muted-foreground border-b">Corporate Name</th>
                    <th className="text-center px-4 py-2 text-xs font-semibold text-muted-foreground border-b">Licenses</th>
                    <th className="text-center px-4 py-2 text-xs font-semibold text-muted-foreground border-b">Contacts</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 20).map((op, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-muted/20'}>
                      <td className="px-4 py-2 font-medium border-b border-border">{op.corporateName || '—'}</td>
                      <td className="px-4 py-2 text-center border-b border-border">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">{op.licenseCount}</span>
                      </td>
                      <td className="px-4 py-2 text-center border-b border-border">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">{op.contacts}</span>
                      </td>
                    </tr>
                  ))}
                  {preview.length > 20 && (
                    <tr><td colSpan={3} className="px-4 py-2 text-center text-xs text-muted-foreground">…and {preview.length - 20} more</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleUpload} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                <CloudUpload className="w-4 h-4 mr-2" />Upload {preview.length} Operator{preview.length !== 1 ? 's' : ''}
              </Button>
              <Button variant="outline" onClick={reset}>Change File</Button>
            </div>
          </div>
        )}

        {step === 'uploading' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="w-12 h-12 animate-spin text-emerald-600" />
            <p className="font-semibold">Uploading & processing…</p>
            <p className="text-sm text-muted-foreground">This may take a moment for large files</p>
          </div>
        )}

        {step === 'results' && result && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <p className="text-3xl font-bold text-emerald-700">{result.created ?? 0}</p>
                <p className="text-xs text-emerald-600 font-medium mt-1">Created</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-blue-50 border border-blue-200">
                <p className="text-3xl font-bold text-blue-700">{result.updated ?? 0}</p>
                <p className="text-xs text-blue-600 font-medium mt-1">Updated</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-red-50 border border-red-200">
                <p className="text-3xl font-bold text-red-700">{result.failed ?? 0}</p>
                <p className="text-xs text-red-600 font-medium mt-1">Failed</p>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1 max-h-48 overflow-y-auto">
                <p className="text-xs font-semibold text-red-700 mb-2">Errors ({result.errors.length})</p>
                {result.errors.map((err, i) => (
                  <p key={i} className="text-xs text-red-600 flex items-start gap-1.5">
                    <XCircle className="w-3 h-3 mt-0.5 shrink-0" />{err}
                  </p>
                ))}
              </div>
            )}
            {(result.created > 0 || result.updated > 0) && result.failed === 0 && (
              <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800 text-sm">
                <CheckCircle2 className="w-5 h-5 shrink-0" />
                <span className="font-medium">All operators processed successfully!</span>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} className="flex-1">Upload Another</Button>
              <Button onClick={handleClose} className="flex-1">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Sanitize form → API payload (empty strings → null for typed fields) ────
function sanitizeLicense(lic) {
  const strOrNull  = v => (v !== '' && v != null) ? v : null;
  const numOrNull  = v => (v !== '' && v != null) ? Number(v) : null;
  const dateOrNull = v => (v && v.trim()) ? v : null;
  return {
    ...lic,
    licenseStateId:     lic.licenseStateId  ?? null,
    addressStateId:     lic.addressStateId  ?? null,
    latitude:           numOrNull(lic.latitude),
    longitude:          numOrNull(lic.longitude),
    issueDate:          dateOrNull(lic.issueDate),
    expirationDate:     dateOrNull(lic.expirationDate),
    firstOccurrence:    dateOrNull(lic.firstOccurrence),
    renewalDueDate:     dateOrNull(lic.renewalDueDate),
    // string fields: send null instead of ""
    licenseNumber:      strOrNull(lic.licenseNumber),
    licenseType:        strOrNull(lic.licenseType),
    licenseDesignation: strOrNull(lic.licenseDesignation),
    licenseStatus:      strOrNull(lic.licenseStatus),
    category:           strOrNull(lic.category),
    vertical:           strOrNull(lic.vertical),
    activityOrFacility: strOrNull(lic.activityOrFacility),
    licenseTerm:        strOrNull(lic.licenseTerm),
    operationalStatus:  strOrNull(lic.operationalStatus),
    businessName:       strOrNull(lic.businessName),
    dbaName:            strOrNull(lic.dbaName),
    email:              strOrNull(lic.email),
    phone:              strOrNull(lic.phone),
    website:            strOrNull(lic.website),
    streetAddress:      strOrNull(lic.streetAddress),
    city:               strOrNull(lic.city),
    county:             strOrNull(lic.county),
    zipCode:            strOrNull(lic.zipCode),
    mailingAddress:     strOrNull(lic.mailingAddress),
    instagram:          strOrNull(lic.instagram),
    facebook:           strOrNull(lic.facebook),
    linkedin:           strOrNull(lic.linkedin),
    twitter:            strOrNull(lic.twitter),
  };
}

function preparePayload(form) {
  return {
    ...form,
    licenses: (form.licenses ?? []).map(sanitizeLicense),
  };
}

// ─── Map API response → form state ───────────────────────────────────────────
function mapLicenseFromApi(l) {
  const dateStr = v => (v ? v.split('T')[0] : '');
  return {
    licenseNumber:      l.licenseNumber      ?? '',
    licenseStateId:     l.licenseStateId     ?? null,
    licenseType:        l.licenseType        ?? '',
    licenseDesignation: l.licenseDesignation ?? '',
    licenseStatus:      l.licenseStatus      ?? '',
    category:           l.category           ?? '',
    vertical:           l.vertical           ?? '',
    activityOrFacility: l.activityOrFacility ?? '',
    licenseTerm:        l.licenseTerm        ?? '',
    operationalStatus:  l.operationalStatus  ?? '',
    issueDate:          dateStr(l.issueDate),
    expirationDate:     dateStr(l.expirationDate),
    firstOccurrence:    dateStr(l.firstOccurrence),
    renewalDueDate:     dateStr(l.renewalDueDate),
    businessName:       l.businessName       ?? '',
    dbaName:            l.dbaName            ?? '',
    email:              l.email              ?? '',
    phone:              l.phone              ?? '',
    website:            l.website            ?? '',
    streetAddress:      l.streetAddress      ?? '',
    city:               l.city               ?? '',
    county:             l.county             ?? '',
    zipCode:            l.zipCode            ?? '',
    addressStateId:     l.addressStateId     ?? null,
    mailingAddress:     l.mailingAddress     ?? '',
    instagram:          l.instagram          ?? '',
    facebook:           l.facebook           ?? '',
    linkedin:           l.linkedin           ?? '',
    twitter:            l.twitter            ?? '',
    latitude:           l.latitude           ?? '',
    longitude:          l.longitude          ?? '',
    contacts: l.contacts?.length
      ? l.contacts.map(c => ({
          contactDesignation: c.contactDesignation ?? '',
          contactFirstName:   c.contactFirstName   ?? '',
          contactLastName:    c.contactLastName    ?? '',
          contactPhone:       c.contactPhone       ?? '',
          contactEmail:       c.contactEmail       ?? '',
        }))
      : [{ ...EMPTY_CONTACT }],
  };
}

function mapOperatorFromApi(full) {
  return {
    operatorId:        full.operatorId,
    corporateName:     full.corporateName     ?? '',
    corpPhone:         full.corpPhone         ?? '',
    corpEmail:         full.corpEmail         ?? '',
    corpWebsite:       full.corpWebsite       ?? '',
    corpFirstName:     full.corpFirstName     ?? '',
    corpLastName:      full.corpLastName      ?? '',
    corpDesignation:   full.corpDesignation   ?? '',
    corpFB:            full.corpFB            ?? '',
    corpInsta:         full.corpInsta         ?? '',
    corpTwitter:       full.corpTwitter       ?? '',
    corpLinkedIn:      full.corpLinkedIn      ?? '',
    notes:             full.notes             ?? '',
    licenseCount:      full.licenseCount,
    licenseStateCount: full.licenseStateCount,
    licensetypeCount:  full.licensetypeCount,
    licenses: full.licenses?.length > 0
      ? full.licenses.map(mapLicenseFromApi)
      : [{ ...EMPTY_LICENSE, contacts: [{ ...EMPTY_CONTACT }] }],
  };
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AdminOperatorsPage() {
  const [data, setData]           = useState({ items: [], total: 0, page: 1, pageSize: 25, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch]       = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage]           = useState(1);
  const [states, setStates]       = useState([]);

  const [editOp, setEditOp]             = useState(null);
  const [isEditOpen, setIsEditOpen]     = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [isSaving, setIsSaving]         = useState(false);

  const [addForm, setAddForm]     = useState(newOperator);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isAdding, setIsAdding]   = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting]     = useState(false);
  const [isBulkOpen, setIsBulkOpen]     = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchOperators = useCallback(async () => {
    setIsLoading(true);
    try {
      const filter = { page, pageSize: 25 };
      if (search)      filter.search = search;
      if (stateFilter) filter.state  = stateFilter;
      const result = await adminService.getOperators(filter);
      setData(result);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to load operators');
    } finally {
      setIsLoading(false);
    }
  }, [search, stateFilter, page]);

  useEffect(() => { fetchOperators(); }, [fetchOperators]);
  useEffect(() => { adminService.getStates().then(setStates).catch(() => {}); }, []);

  // ── Edit ──────────────────────────────────────────────────────────────────
  const openEdit = async (op) => {
    setIsEditOpen(true); setIsLoadingEdit(true);
    try {
      const full = await adminService.getOperator(op.operatorId);
      setEditOp(mapOperatorFromApi(full));
    } catch { toast.error('Failed to load operator'); setIsEditOpen(false); }
    finally { setIsLoadingEdit(false); }
  };

  const handleSave = async () => {
    if (!editOp?.corporateName?.trim()) { toast.error('Corporate Name is required'); return; }
    setIsSaving(true);
    try {
      await adminService.updateOperator(editOp.operatorId, preparePayload(editOp));
      toast.success('Operator updated'); setIsEditOpen(false); fetchOperators();
    } catch (err) { toast.error(typeof err === 'string' ? err : 'Failed to update'); }
    finally { setIsSaving(false); }
  };

  const upEditLic  = (i, v) => setEditOp(p => ({ ...p, licenses: p.licenses.map((l, j) => j === i ? v : l) }));
  const rmEditLic  = i       => setEditOp(p => ({ ...p, licenses: p.licenses.filter((_, j) => j !== i) }));
  const addEditLic = ()      => setEditOp(p => ({ ...p, licenses: [...p.licenses, { ...EMPTY_LICENSE, contacts: [{ ...EMPTY_CONTACT }] }] }));

  // ── Add ───────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!addForm.corporateName?.trim()) { toast.error('Corporate Name is required'); return; }
    setIsAdding(true);
    try {
      await adminService.createOperator(preparePayload(addForm));
      toast.success('Operator created'); setIsAddOpen(false); setAddForm(newOperator()); fetchOperators();
    } catch (err) { toast.error(typeof err === 'string' ? err : 'Failed to create'); }
    finally { setIsAdding(false); }
  };

  const upAddLic  = (i, v) => setAddForm(p => ({ ...p, licenses: p.licenses.map((l, j) => j === i ? v : l) }));
  const rmAddLic  = i       => setAddForm(p => ({ ...p, licenses: p.licenses.filter((_, j) => j !== i) }));
  const addAddLic = ()      => setAddForm(p => ({ ...p, licenses: [...p.licenses, { ...EMPTY_LICENSE, contacts: [{ ...EMPTY_CONTACT }] }] }));

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await adminService.deleteOperator(deleteTarget.operatorId);
      toast.success(`"${deleteTarget.corporateName}" deleted`); setDeleteTarget(null); fetchOperators();
    } catch (err) { toast.error(typeof err === 'string' ? err : 'Delete failed'); }
    finally { setIsDeleting(false); }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="space-y-5">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Operators</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Manage operator records, licenses, and contacts</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={fetchOperators}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
            <Button variant="outline" size="sm" onClick={() => setIsBulkOpen(true)}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />Bulk Import
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => { setAddForm(newOperator()); setIsAddOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />Add Operator
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <form onSubmit={e => { e.preventDefault(); setPage(1); fetchOperators(); }}
              className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search by corporate name, business name, license…"
                  value={search} onChange={e => setSearch(e.target.value)} className="pl-10" />
              </div>
              <Select value={stateFilter || 'all'} onValueChange={v => { setStateFilter(v === 'all' ? '' : v); setPage(1); }}>
                <SelectTrigger className="w-44"><SelectValue placeholder="All States" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {states.map(s => <SelectItem key={s.stateId} value={s.abbreviation}>{s.abbreviation} — {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="submit">Search</Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Operators</CardTitle>
                <CardDescription className="mt-0.5">{data.total} total operator{data.total !== 1 ? 's' : ''}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="rounded-md border overflow-hidden">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-muted/40 border-b">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Corporate Name</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Corp Contact</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Primary Person</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">States</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Licenses</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground">Types</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items?.map(op => {
                        const licStates = [...new Set((op.licenses || []).map(l => l.licenseStateAbbr).filter(Boolean))];
                        return (
                          <tr key={op.operatorId} className="border-b border-border hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-3">
                              <p className="font-semibold text-foreground">{op.corporateName || '—'}</p>
                              {op.licenses?.[0]?.businessName && op.licenses[0].businessName !== op.corporateName && (
                                <p className="text-xs text-muted-foreground mt-0.5">{op.licenses[0].businessName}</p>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {op.corpPhone && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Phone className="w-3 h-3 shrink-0" />{op.corpPhone}
                                </div>
                              )}
                              {op.corpEmail && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                                  <Mail className="w-3 h-3 shrink-0" />
                                  <span className="truncate max-w-[160px]">{op.corpEmail}</span>
                                </div>
                              )}
                              {!op.corpPhone && !op.corpEmail && <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-4 py-3">
                              {(op.corpFirstName || op.corpLastName) ? (
                                <div>
                                  <p className="text-sm font-medium">{[op.corpFirstName, op.corpLastName].filter(Boolean).join(' ')}</p>
                                  {op.corpDesignation && <p className="text-xs text-muted-foreground">{op.corpDesignation}</p>}
                                </div>
                              ) : <span className="text-muted-foreground">—</span>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {licStates.length > 0 ? (
                                <div className="flex flex-wrap gap-1 justify-center">
                                  {licStates.slice(0, 3).map(s => (
                                    <Badge key={s} variant="secondary" className="text-xs px-1.5">{s}</Badge>
                                  ))}
                                  {licStates.length > 3 && (
                                    <Badge variant="outline" className="text-xs px-1.5">+{licStates.length - 3}</Badge>
                                  )}
                                </div>
                              ) : (
                                <Badge variant="secondary" className="text-xs">
                                  {op.licenseStateCount ?? 0} state{(op.licenseStateCount ?? 0) !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold">
                                {op.licenseCount ?? op.licenses?.length ?? 0}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">
                                {op.licensetypeCount ?? 0}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-1 justify-end">
                                <Button variant="ghost" size="sm" onClick={() => openEdit(op)} title="Edit" className="h-8 w-8 p-0">
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(op)}
                                  className="h-8 w-8 p-0 text-destructive hover:text-destructive" title="Delete">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      {!data.items?.length && (
                        <tr>
                          <td colSpan={7} className="text-center py-16 text-muted-foreground">
                            <Building2 className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            No operators found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.page} of {data.totalPages} ({data.total} records)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= data.totalPages} onClick={() => setPage(p => p + 1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Bulk Upload ──────────────────────────────────────────────────────── */}
      <BulkUploadModal open={isBulkOpen} onClose={() => setIsBulkOpen(false)} onComplete={fetchOperators} />

      {/* ── Edit Dialog ──────────────────────────────────────────────────────── */}
      {isLoadingEdit ? (
        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
          <DialogContent className="max-w-3xl">
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </DialogContent>
        </Dialog>
      ) : (
        <OperatorDialog
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
          title="Edit Operator"
          description="Corporate info, licenses, and per-license contacts"
          form={editOp}
          setForm={setEditOp}
          onSave={handleSave}
          saving={isSaving}
          states={states}
          addLicense={addEditLic}
          upLicense={upEditLic}
          rmLicense={rmEditLic}
        />
      )}

      {/* ── Add Dialog ───────────────────────────────────────────────────────── */}
      <OperatorDialog
        open={isAddOpen}
        onOpenChange={v => { if (!isAdding) { setIsAddOpen(v); if (!v) setAddForm(newOperator()); } }}
        title="Add Operator"
        description="Corporate Name is required. Each license can have multiple contacts."
        form={addForm}
        setForm={setAddForm}
        onSave={handleAdd}
        saving={isAdding}
        states={states}
        addLicense={addAddLic}
        upLicense={upAddLic}
        rmLicense={rmAddLic}
      />

      {/* ── Delete Confirm ───────────────────────────────────────────────────── */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />Delete Operator
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteTarget?.corporateName}</strong>?
              This will also remove all their licenses and contacts. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
